import type { Budget } from "@station/contracts";
import {
  Alert,
  Button,
  FormField,
  Input,
  SkeletonRows,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FileChartColumn } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Amount } from "../../../features/accounting/shared.js";
import { BudgetGrid } from "../../../features/budget/budget-grid.js";
import { BudgetImportPanel } from "../../../features/budget/import-panel.js";
import {
  useBudget,
  useBudgetLines,
  useBudgetPermissions,
  useRefreshBudgets,
} from "../../../features/budget/queries.js";
import { BudgetStatusBadge, useBudgetCrumb } from "../../../features/budget/shared.js";
import { ExpensesPage, Field, Section } from "../../../features/expenses/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";

const TABS = ["lines", "import"] as const;
const searchSchema = z.object({ tab: z.enum(TABS).optional().catch(undefined) });

export const Route = createFileRoute("/_app/butce/$id")({
  validateSearch: (search) => searchSchema.parse(search),
  component: BudgetDetailPage,
});

function BudgetDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const crumb = useBudgetCrumb();
  const budget = useBudget(id);
  const title = budget.data?.name ?? t("budget.detailTitle");

  return (
    <ExpensesPage title={title} breadcrumb={[crumb, { label: title }]}>
      {() =>
        budget.error ? (
          <Alert tone="danger">{errorMessage(budget.error)}</Alert>
        ) : !budget.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <BudgetDetail budget={budget.data} />
        )
      }
    </ExpensesPage>
  );
}

function BudgetDetail({ budget }: { budget: Budget }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const permissions = useBudgetPermissions();
  const lines = useBudgetLines(budget.id);
  const tab = search.tab ?? "lines";
  // Etkin ve arşivlenmiş bütçenin satırları DEĞİŞMEZ; revizyon yeni sürümle yapılır.
  const readOnly = budget.status !== "DRAFT" || !permissions.canManage;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-lg font-semibold">{budget.name}</span>
        <BudgetStatusBadge status={budget.status} />
        <span className="text-sm text-muted-foreground">
          {t("budget.fields.fiscalYear")}: {budget.fiscalYear} · {t("budget.fields.versionNo")}:{" "}
          {budget.versionNo}
        </span>
        <Button variant="outline" className="ms-auto" asChild>
          <Link to="/butce/rapor" search={{ budgetId: budget.id }}>
            <FileChartColumn aria-hidden="true" />
            {t("budget.report.title")}
          </Link>
        </Button>
      </div>

      <Section title={t("budget.detailSummary")}>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("budget.fields.lineCount")}>{String(budget.lineCount)}</Field>
          <Field label={t("budget.fields.totalAmount")}>
            <Amount value={budget.totalAmount} />
          </Field>
          <Field label={t("budget.fields.variancePercent")}>
            %{budget.variancePercent.replace(".", ",")}
          </Field>
          <Field label={t("budget.fields.notes")}>{budget.notes ?? "—"}</Field>
        </dl>
        {budget.status === "DRAFT" && permissions.canManage ? <HeaderForm budget={budget} /> : null}
      </Section>

      <Tabs
        value={tab}
        onValueChange={(next) =>
          void navigate({ search: { tab: next as (typeof TABS)[number] }, replace: true })
        }
      >
        <TabsList aria-label={t("budget.detailTabs")}>
          <TabsTrigger value="lines">{t("budget.lines.title")}</TabsTrigger>
          <TabsTrigger value="import" disabled={readOnly}>
            {t("budget.import.title")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="lines">
          {tab === "lines" ? (
            lines.error ? (
              <Alert tone="danger">{errorMessage(lines.error)}</Alert>
            ) : !lines.data ? (
              <SkeletonRows rows={6} />
            ) : (
              <BudgetGrid
                key={`${budget.id}-${budget.version}`}
                budget={budget}
                lines={lines.data.items}
                warnings={lines.data.warnings}
                readOnly={readOnly}
              />
            )
          ) : null}
        </TabsContent>
        <TabsContent value="import">
          {tab === "import" && !readOnly ? <BudgetImportPanel budget={budget} /> : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** Taslak bütçenin başlık alanları (ad, sapma eşiği, not). */
function HeaderForm({ budget }: { budget: Budget }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshBudgets();
  const [state, setState] = useState({
    name: budget.name,
    variancePercent: budget.variancePercent,
    notes: budget.notes ?? "",
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () =>
      api.PATCH("/budgets/{id}", {
        params: { path: { id: budget.id } },
        body: {
          name: state.name.trim(),
          variancePercent: state.variancePercent.trim().replace(",", "."),
          notes: state.notes.trim() || null,
          version: budget.version,
        } as never,
      }),
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("common.saved") });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <form
      className="grid gap-3 border-t pt-3"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label={t("budget.fields.name")} required>
          {(control) => (
            <Input
              {...control}
              required
              value={state.name}
              maxLength={150}
              onChange={(event) => setState({ ...state, name: event.target.value })}
            />
          )}
        </FormField>
        <FormField
          label={t("budget.fields.variancePercent")}
          description={t("budget.fields.variancePercentHint")}
        >
          {(control) => (
            <Input
              {...control}
              inputMode="decimal"
              value={state.variancePercent}
              onChange={(event) => setState({ ...state, variancePercent: event.target.value })}
            />
          )}
        </FormField>
      </div>
      <FormField label={t("budget.fields.notes")}>
        {(control) => (
          <Textarea
            {...control}
            value={state.notes}
            maxLength={1000}
            onChange={(event) => setState({ ...state, notes: event.target.value })}
          />
        )}
      </FormField>
      <Button type="submit" variant="outline" className="justify-self-start" loading={save.isPending}>
        {t("budget.saveHeader")}
      </Button>
    </form>
  );
}
